import type { BillingMode, BillingPaymentTerm, BillingTier } from '@zeus/index'
import { $, Selector } from '@zeus/index'
import { typedGql } from '@zeus/typedDocumentNode'
import { scalars } from '~/gql/utils/scalars'

const countersSelector = Selector('LearnerCountersModel')({
  orgId: true,
  orgName: true,
  orgSlug: true,
  month: true,
  monthlyActive: true,
  learnerCount: true,
  registeredLearners: true,
  leavingWithinThreeMonths: true,
  subscribedTier: true,
  effectiveTier: true,
  billedTier: true,
  placesRemaining: true,
  nextTier: true,
  paymentTerm: true,
  estimatedRevenueCents: true,
  overCapacity: true,
})

/** Compteurs de toutes les organisations (SuperAdmin). */
export const GQL_LEARNER_COUNTERS_BY_ORGANIZATION = typedGql('query')({
  learnerCountersByOrganization: [{ month: $('month', 'String') }, countersSelector],
})

/** Compteurs de l'organisation de l'admin connecté. */
export const GQL_LEARNER_COUNTERS = typedGql('query')({
  learnerCounters: [{ month: $('month', 'String') }, countersSelector],
})

const accountFields = {
  id: true,
  tier: true,
  paymentTerm: true,
  mode: true,
  tierChangedAt: true,
  subscriptionStatus: true,
  subscribed: true,
  currentPeriodEnd: true,
  cancelAtPeriodEnd: true,
  scheduledTier: true,
  scheduledPaymentTerm: true,
  scheduledChangeAt: true,
  cancelAt: true,
  billingPortalAvailable: true,
  companyName: true,
  addressLine1: true,
  addressLine2: true,
  postalCode: true,
  city: true,
  country: true,
  vatNumber: true,
  billingEmail: true,
} as const

/** Palier et coordonnées de facturation de l'organisation (Admin). */
export const GQL_BILLING_ACCOUNT = typedGql('query', { scalars })({
  billingAccount: accountFields,
})

/** Le paiement en échec seul, pour le bandeau du back-office (#354). */
export const GQL_PAYMENT_FAILED = typedGql('query')({
  billingAccount: { id: true, paymentFailed: true },
})

/** Ce que le palier coûte sur le mois observé. */
export const GQL_BILLING_ESTIMATE = typedGql('query')({
  billingEstimate: [{ month: $('month', 'String') }, {
    month: true,
    subscribedTier: true,
    effectiveTier: true,
    billedTier: true,
    paymentTerm: true,
    learnerCount: true,
    monthlyActive: true,
    registeredLearners: true,
    placesRemaining: true,
    nextTier: true,
    leavingWithinThreeMonths: true,
    monthlyPriceCents: true,
    monthlyAmountCents: true,
    upfrontYearlyAmountCents: true,
    isCustom: true,
    managed: true,
    overCapacity: true,
  }],
})

/** Historique mensuel des actifs, du compteur et des montants. */
export const GQL_BILLING_HISTORY = typedGql('query')({
  billingHistory: [{ months: $('months', 'Int') }, {
    month: true,
    monthlyActive: true,
    learnerCount: true,
    tier: true,
    amountCents: true,
  }],
})

/** Dépassement de capacité dont les admins ont été prévenus par mail (#355) ; `null` sinon. */
export const GQL_CAPACITY_ALERT = typedGql('query')({
  capacityAlert: { learnerCount: true, tier: true, neededTier: true },
})

export const GQL_UPDATE_BILLING_PROFILE = typedGql('mutation', { scalars })({
  updateBillingProfile: [{ input: $('input', 'UpdateBillingProfileInput!') }, accountFields],
})

/** La grille des paliers vendus en ligne, en centimes HT (#352). */
export const GQL_BILLING_OFFERS = typedGql('query')({
  billingOffers: { tier: true, capacity: true, monthlyPriceCents: true, upfrontYearlyAmountCents: true },
})

/** L'URL du portail client Stripe : palier, résiliation, carte, factures (#353). */
export const GQL_OPEN_BILLING_PORTAL = typedGql('mutation')({
  openBillingPortal: true,
})

/** L'URL de la page de paiement Stripe pour un palier et une échéance (#352). */
export const GQL_START_CHECKOUT = typedGql('mutation')({
  startCheckout: [{ input: $('input', 'StartCheckoutInput!') }, true],
})

// Types concrets pour le rendu : les `InputType` zeus résolvent leurs champs en
// `unknown` (inexploitables en accès de champ), d'où ces formes explicites.
export type LearnerCountersRow = {
  orgId: string
  orgName: string
  orgSlug: string
  month: string
  monthlyActive: number
  learnerCount: number
  registeredLearners: number
  leavingWithinThreeMonths: number
  subscribedTier: BillingTier
  effectiveTier: BillingTier
  billedTier: BillingTier
  placesRemaining?: number | null
  nextTier?: BillingTier | null
  paymentTerm: BillingPaymentTerm
  estimatedRevenueCents: number
  overCapacity: boolean
}

export type BillingAccount = {
  id: string
  tier: BillingTier
  paymentTerm: BillingPaymentTerm
  mode: BillingMode
  tierChangedAt?: Date | null
  /** Statut Stripe tel quel (active, past_due…), absent sans abonnement. */
  subscriptionStatus?: string | null
  /** L'abonnement tient-il son palier ? Calculé par l'API (#352). */
  subscribed: boolean
  /** Prochaine échéance : renouvellement, ou fin de l'abonnement s'il est résilié. */
  currentPeriodEnd?: Date | null
  cancelAtPeriodEnd: boolean
  /** Changement programmé à l'échéance depuis le portail (#353). */
  scheduledTier?: BillingTier | null
  scheduledPaymentTerm?: BillingPaymentTerm | null
  scheduledChangeAt?: Date | null
  /** Date d'arrêt d'un abonnement résilié, à l'échéance ou non. */
  cancelAt?: Date | null
  /** L'organisation a souscrit au moins une fois et n'est pas gérée : le portail a des factures à montrer. */
  billingPortalAvailable: boolean
  companyName?: string | null
  addressLine1?: string | null
  addressLine2?: string | null
  postalCode?: string | null
  city?: string | null
  country?: string | null
  vatNumber?: string | null
  billingEmail?: string | null
}

export type BillingOffer = {
  tier: BillingTier
  capacity: number
  monthlyPriceCents: number
  upfrontYearlyAmountCents: number
}

export type BillingEstimate = {
  month: string
  subscribedTier: BillingTier
  effectiveTier: BillingTier
  billedTier: BillingTier
  paymentTerm: BillingPaymentTerm
  learnerCount: number
  monthlyActive: number
  registeredLearners: number
  placesRemaining?: number | null
  nextTier?: BillingTier | null
  leavingWithinThreeMonths: number
  monthlyPriceCents?: number | null
  monthlyAmountCents?: number | null
  upfrontYearlyAmountCents?: number | null
  isCustom: boolean
  managed: boolean
  overCapacity: boolean
}

export type BillingHistoryEntry = {
  month: string
  monthlyActive: number
  learnerCount: number
  tier: BillingTier
  amountCents: number
}
